import React, { ComponentType, useEffect } from "react";
import { trackEvent } from "../utils/analytics";

interface TrackingProps {
  componentName: string;
  [key: string]: any;
}

// HOC to track component mount/unmount and props changes
export const withTracking = <P extends object>(
  WrappedComponent: ComponentType<P>,
  options: {
    trackMount?: boolean;
    trackUnmount?: boolean;
    trackPropsChanges?: boolean;
    componentName?: string;
  } = {
    trackMount: true,
    trackUnmount: false,
    trackPropsChanges: false,
  }
) => {
  const displayName =
    options.componentName ||
    WrappedComponent.displayName ||
    WrappedComponent.name ||
    "Component";

  const TrackedComponent: React.FC<P & TrackingProps> = (props) => {
    const {
      trackMount = true,
      trackUnmount = false,
      trackPropsChanges = false,
    } = options;

    useEffect(() => {
      if (trackMount) {
        trackEvent("component_mount", {
          component_name: displayName,
          component_path: window.location.pathname,
        });
      }

      return () => {
        if (trackUnmount) {
          trackEvent("component_unmount", {
            component_name: displayName,
            component_path: window.location.pathname,
          });
        }
      };
    }, []);

    useEffect(() => {
      if (trackPropsChanges) {
        trackEvent("component_props_change", {
          component_name: displayName,
          component_path: window.location.pathname,
        });
      }
    }, [props]);

    return <WrappedComponent {...(props as P)} />;
  };

  TrackedComponent.displayName = `withTracking(${displayName})`;
  return TrackedComponent;
};

// Hook for tracking interaction within functional components
export const useTracking = (componentName: string) => {
  // Track component mount
  useEffect(() => {
    trackEvent("component_mount", {
      component_name: componentName,
      component_path: window.location.pathname,
    });

    return () => {
      trackEvent("component_unmount", {
        component_name: componentName,
        component_path: window.location.pathname,
      });
    };
  }, [componentName]);

  // Return tracking functions
  return {
    trackAction: (action: string, data: Record<string, any> = {}) => {
      trackEvent("component_action", {
        component_name: componentName,
        action,
        ...data,
      });
    },
    trackComponentView: () => {
      trackEvent("component_view", {
        component_name: componentName,
        component_path: window.location.pathname,
      });
    },
  };
};

// Utility function for form element tracking
export const trackFormElement = (
  formName: string,
  elementName: string,
  action: "focus" | "blur" | "change" | "submit" | "error",
  value?: any
) => {
  trackEvent("form_element_interaction", {
    form_name: formName,
    element_name: elementName,
    action,
    value: typeof value === "object" ? JSON.stringify(value) : value,
  });
};
